import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { getColor } from 'helpers/utils';
import useMarketingCatalogos from '../hooks/useMarketingCatalogos';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  LegendComponent
]);

const PostsBySocialNetworkChart = ({ height }) => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedStatus, setSelectedStatus] = useState('todos');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const { statusOptions } = useMarketingCatalogos();
    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();
    const abortRef = useRef(null);

    const currentYear = new Date().getFullYear();
    const years = useMemo(() => {
        const yearList = [];
        for (let year = currentYear; year >= 2020; year--) {
            yearList.push(year);
        }
        return yearList;
    }, [currentYear]);

    const months = useMemo(() => ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'], []);

    const socialNetworks = useMemo(() => [
        { name: 'Facebook', color: '#1877F2' },
        { name: 'Instagram', color: '#E4405F' },
        { name: 'YouTube', color: '#FF0000' },
        { name: 'LinkedIn', color: '#0A66C2' },
        { name: 'TikTok', color: '#000000' }
    ], []);

    useEffect(() => {
        if (!token) return;

        const fetchData = async () => {
            if (abortRef.current) abortRef.current.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            setLoading(true);
            try {
                const estadoParam = selectedStatus === 'todos' ? '0' : selectedStatus;
                
                const response = await fetch(
                    `/api/marketing/ObtenerPostsPorRedSocial?anio=${selectedYear}&estado=${estadoParam}`,
                    {
                        method: 'GET',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        },
                        signal: controller.signal
                    }
                );

                if (response.ok) {
                    const result = await response.json();
                    if (result.iResponseType === 1 && result.detalles) {
                        const dataset = JSON.parse(result.detalles);
                        const rows = dataset.table2 || [];

                        const monthlyData = months.map((month, index) => {
                            const monthData = { month };
                            const mesNumber = index + 1;
                            
                            socialNetworks.forEach(network => {
                                monthData[network.name] = 0;
                            });

                            const monthRows = rows.filter(r => r.iMes === mesNumber);
                            
                            monthRows.forEach(row => {
                                const network = socialNetworks.find(n => 
                                    n.name.toLowerCase() === (row.vRedSocial || '').toLowerCase()
                                );
                                if (network) {
                                    monthData[network.name] = parseInt(row.iTotalPosts) || 0;
                                }
                            });

                            return monthData;
                        });

                        setData(monthlyData);
                    } else {
                        showErrorToast(result.vMessage || 'Error al obtener datos');
                        setData([]);
                    }
                } else {
                    if (response.status === 401) {
                        setToken(null);
                        location.href = '';
                    }
                    showErrorToast(`Error ${response.status}: ${response.statusText}`);
                    setData([]);
                }
            } catch (error) {
                if (error.name === 'AbortError') return;
                showErrorToast('Error al obtener los datos: ' + error.message);
                console.error('Error fetching posts by social network:', error);
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, [selectedYear, selectedStatus, token]);

    const chartOptions = useMemo(() => ({
        animation: true,
        tooltip: {
            trigger: 'axis',
            padding: [7, 10],
            backgroundColor: getColor('gray-100'),
            borderColor: getColor('gray-300'),
            textStyle: { color: getColor('dark') },
            borderWidth: 1,
            confine: true,
            axisPointer: {
                type: 'line'
            },
            formatter: function (params) {
                let result = `<strong>${params[0].axisValue}</strong><br/>`;
                params.forEach(param => {
                    const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>`;
                    result += `${marker}${param.seriesName}: <b>${param.value}</b> posts<br/>`;
                });
                return result;
            }
        },
        legend: {
            data: socialNetworks.map(a => a.name),
            textStyle: { color: getColor('gray-700') },
            top: '0%',
            type: 'scroll'
        },
        grid: { 
            top: '15%', 
            right: '3%', 
            bottom: '10%', 
            left: '8%',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            data: months,
            boundaryGap: false,
            axisLine: {
                lineStyle: {
                    color: getColor('gray-300')
                }
            },
            axisLabel: {
                color: getColor('gray-600'),
                fontSize: 11
            }
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                color: getColor('gray-600'),
                formatter: '{value}'
            },
            splitLine: {
                lineStyle: {
                    color: getColor('gray-200'),
                    type: 'dashed'
                }
            },
            name: 'Número de Posts',
            nameTextStyle: {
                color: getColor('gray-600'),
                fontSize: 12
            }
        },
        series: socialNetworks.map(network => ({
            name: network.name,
            type: 'line',
            smooth: true,
            data: data.map(d => d[network.name]),
            itemStyle: {
                color: network.color
            },
            lineStyle: {
                width: 3
            },
            symbol: 'circle',
            symbolSize: 8,
            emphasis: {
                focus: 'series',
                lineStyle: {
                    width: 4
                },
                symbolSize: 10
            }
        }))
    }), [data, months, socialNetworks]);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 8 }}>
                <Form.Select 
                    size="sm" 
                    value={selectedStatus} 
                    style={{ width: 180 }} 
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    disabled={loading}
                >
                    {statusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Form.Select>
                <Form.Select 
                    size="sm" 
                    value={selectedYear} 
                    style={{ width: 120 }} 
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    disabled={loading}
                >
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </Form.Select>
            </div>
            {loading ? (
                <div style={{ 
                    height: `${height}px`, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    color: getColor('gray-600')
                }}>
                    Cargando datos...
                </div>
            ) : (
                <ReactEChartsCore 
                    echarts={echarts} 
                    option={chartOptions} 
                    style={{ height: `${height}px` }}
                    opts={{ renderer: 'canvas' }}
                />
            )}
        </div>
    );
};

PostsBySocialNetworkChart.propTypes = {
    height: PropTypes.number
};

PostsBySocialNetworkChart.defaultProps = {
    height: 400
};

export default PostsBySocialNetworkChart;
